import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import { fetchGames } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Camera, AlertTriangle, Reply, Trash2, ChevronDown,
  Clock, Image as ImageIcon, X, Loader2, Megaphone, Users, Flag, Ban, MoreVertical
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

export default function MessagesPage() {
  const navigate = useNavigate();
  const { token, user } = useAuthStore();
  const { currentTeamId, currentGameId } = useGameStore();
  const teamId = currentTeamId;

  const [messages, setMessages] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'urgent' | game_id
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [expandedMsg, setExpandedMsg] = useState(null);

  // Compose state
  const [msgText, setMsgText] = useState('');
  const [msgPhoto, setMsgPhoto] = useState(null);
  const [msgPhotoPreview, setMsgPhotoPreview] = useState(null);
  const [msgIsUrgent, setMsgIsUrgent] = useState(false);
  // Pin-on-send toggle for the compose dialog. Smart auto-suggest sets this
  // to true when the message body matches keywords like "rain", "field change",
  // "moved to" — coaches can always uncheck before sending.
  const [msgPinOnSend, setMsgPinOnSend] = useState(false);
  const [pinAutoSuggested, setPinAutoSuggested] = useState(false);
  const [msgGameId, setMsgGameId] = useState('');

  // Reply state
  const [replyText, setReplyText] = useState('');
  const [replyPhoto, setReplyPhoto] = useState(null);
  const [replyPhotoPreview, setReplyPhotoPreview] = useState(null);

  // Safety state (report / block)
  const [reportTarget, setReportTarget] = useState(null); // { messageId, replyId?, authorId, authorName }
  const [reportReason, setReportReason] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  const fileInputRef = useRef(null);
  const replyFileRef = useRef(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!teamId) { navigate('/dashboard'); return; }
    loadMessages();
    loadGames();
    const interval = setInterval(loadMessages, 15000);
    return () => clearInterval(interval);
  }, [teamId]);

  const loadMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/teams/${teamId}/messages`, { headers });
      if (res.ok) setMessages(await res.json());
    } catch {} finally { setLoading(false); }
  }, [teamId, token]);

  const loadGames = async () => {
    try {
      const data = await fetchGames(teamId);
      setGames(data);
    } catch {}
  };

  const handlePhotoSelect = (e, isReply = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (isReply) {
        setReplyPhoto(reader.result);
        setReplyPhotoPreview(reader.result);
      } else {
        setMsgPhoto(reader.result);
        setMsgPhotoPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  /**
   * Detects messages that look like critical coach announcements based on
   * common keywords. Used to auto-suggest pinning during compose.
   */
  const PIN_SUGGEST_PATTERNS = [
    /field\s*change/i,
    /\bmoved?\s*(to|from)\b/i,
    /\b(rain|storm|lightning|thunder)\b/i,
    /\b(cancell?ed|postponed|delayed?)\b/i,
    /\bfield\s*\d+\b/i,        // "field 4", "field 12"
    /\btime\s*change\b/i,
    /\bnew\s*location\b/i,
    /\b(snack|carpool)\s*(schedule|sign-?up)\b/i,
  ];
  const shouldSuggestPin = (text) => {
    if (!text || text.length < 6) return false;
    return PIN_SUGGEST_PATTERNS.some(rx => rx.test(text));
  };

  // Watch the compose textarea — if it newly matches a pin-suggest pattern
  // AND the coach hasn't manually overridden the toggle yet, gently flip it on.
  // We track `pinAutoSuggested` so a coach who manually unchecks isn't fought.
  React.useEffect(() => {
    if (!composeOpen) return;
    if (shouldSuggestPin(msgText)) {
      if (!pinAutoSuggested && !msgPinOnSend) {
        setMsgPinOnSend(true);
        setPinAutoSuggested(true);
      }
    } else if (pinAutoSuggested && msgPinOnSend) {
      // Pattern no longer matches → only revert if WE auto-suggested it
      setMsgPinOnSend(false);
      setPinAutoSuggested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgText, composeOpen]);

  const handleSendMessage = async () => {
    if (!msgText.trim() && !msgPhoto) { toast.error('Enter a message or attach a photo'); return; }
    setSending(true);
    try {
      const body = {
        text: msgText.trim(),
        photo: msgPhoto,
        is_urgent: msgIsUrgent,
        game_id: msgGameId || null
      };
      const res = await fetch(`${API}/api/teams/${teamId}/messages`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
      const created = await res.json().catch(() => null);
      // If the coach chose to pin-on-send (or auto-suggest left the toggle on),
      // fire a follow-up pin request. Fail-soft: the message is already sent.
      if (msgPinOnSend && created?.id) {
        try {
          await fetch(`${API}/api/messages/${created.id}/pin`, {
            method: 'POST', headers, body: JSON.stringify({ pinned: true }),
          });
        } catch (e) { /* pin is best-effort, message already sent */ console.warn('pin-on-send failed', e); }
      }
      toast.success(
        msgPinOnSend
          ? '📌 Pinned to Parent View'
          : msgIsUrgent ? 'Urgent message sent!' : 'Message sent!'
      );
      setComposeOpen(false);
      setMsgText(''); setMsgPhoto(null); setMsgPhotoPreview(null); setMsgIsUrgent(false); setMsgGameId('');
      setMsgPinOnSend(false); setPinAutoSuggested(false);
      loadMessages();
    } catch (err) {
      toast.error(err.message);
    } finally { setSending(false); }
  };

  const handleSendReply = async (messageId) => {
    if (!replyText.trim() && !replyPhoto) { toast.error('Enter a reply'); return; }
    setSending(true);
    try {
      const body = { text: replyText.trim(), photo: replyPhoto };
      const res = await fetch(`${API}/api/messages/${messageId}/reply`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Failed to send reply');
      toast.success('Reply sent!');
      setReplyingTo(null); setReplyText(''); setReplyPhoto(null); setReplyPhotoPreview(null);
      loadMessages();
    } catch (err) {
      toast.error(err.message);
    } finally { setSending(false); }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const res = await fetch(`${API}/api/messages/${messageId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Message deleted');
      loadMessages();
    } catch (err) { toast.error(err.message); }
  };

  /**
   * Pin / unpin a coach message. Pinned messages float to the top of Parent View
   * as a sticky banner. Auto-unpins after 7 days on the backend so the banner
   * never goes stale.
   */
  const handleTogglePin = async (messageId, currentlyPinned) => {
    try {
      const res = await fetch(`${API}/api/messages/${messageId}/pin`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ pinned: !currentlyPinned }),
      });
      if (!res.ok) throw new Error('Failed to update pin');
      toast.success(currentlyPinned ? 'Unpinned' : 'Pinned to parent view');
      loadMessages();
    } catch (err) { toast.error(err.message); }
  };

  const submitReport = async () => {
    if (!reportTarget) return;
    try {
      const res = await fetch(`${API}/api/messages/${reportTarget.messageId}/report`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          reply_id: reportTarget.replyId || null,
          reason: reportReason || 'not_specified',
        }),
      });
      if (!res.ok) throw new Error('Failed to submit report');
      const body = await res.json();
      toast.success(body.status === 'already_reported' ? 'Already reported — thanks' : 'Reported. Our team will review.');
      setReportTarget(null);
      setReportReason('');
    } catch (err) { toast.error(err.message); }
  };

  const blockAuthor = async (targetUserId, targetName) => {
    if (!targetUserId) return;
    if (!window.confirm(`Block ${targetName || 'this user'}? You won't see their messages or replies.`)) return;
    try {
      const res = await fetch(`${API}/api/users/block`, {
        method: 'POST', headers,
        body: JSON.stringify({ target_user_id: targetUserId }),
      });
      if (!res.ok) throw new Error('Failed to block user');
      toast.success(`Blocked ${targetName || 'user'}`);
      setOpenMenuId(null);
      loadMessages();
    } catch (err) { toast.error(err.message); }
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'all') return true;
    if (filter === 'urgent') return m.is_urgent;
    return m.game_id === filter;
  });

  const gameMap = {};
  games.forEach(g => { gameMap[g.id] = g; });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4 md:p-6" data-testid="messages-page">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              TEAM MESSAGES
            </h1>
            <p className="text-stone-500 text-sm">{messages.length} message{messages.length !== 1 ? 's' : ''}</p>
          </div>
          <Button onClick={() => setComposeOpen(true)} className="bg-green-700 hover:bg-green-600" data-testid="compose-message-btn">
            <Megaphone size={16} className="mr-2" /> New Message
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="message-filters">
          <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-green-700' : ''}
            onClick={() => setFilter('all')} data-testid="filter-all">
            All
          </Button>
          <Button size="sm" variant={filter === 'urgent' ? 'default' : 'outline'}
            className={filter === 'urgent' ? 'bg-red-600' : 'border-red-300 text-red-600'}
            onClick={() => setFilter('urgent')} data-testid="filter-urgent">
            <AlertTriangle size={14} className="mr-1" /> Urgent
          </Button>
          {games.slice(0, 5).map(g => (
            <Button key={g.id} size="sm" variant={filter === g.id ? 'default' : 'outline'}
              className={filter === g.id ? 'bg-stone-700' : ''}
              onClick={() => setFilter(g.id)}>
              {g.is_home ? 'vs' : '@'} {g.opponent_name}
            </Button>
          ))}
        </div>

        {/* Messages List */}
        {filteredMessages.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <MessageSquare size={40} className="mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">No messages yet</p>
              <p className="text-stone-400 text-sm mt-1">Send the first announcement to your team!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="messages-list">
            {filteredMessages.map(msg => {
              const isExpanded = expandedMsg === msg.id;
              const game = msg.game_id ? gameMap[msg.game_id] : null;
              const isAuthor = msg.author_id === user?.id;

              return (
                <Card key={msg.id} className={cn(
                  "border-0 shadow-sm transition-all",
                  msg.is_urgent && "border-l-4 border-l-red-500 bg-red-50/30",
                  msg.pinned && !msg.is_urgent && "border-l-4 border-l-amber-500 bg-amber-50/30"
                )} data-testid={`message-${msg.id}`}>
                  <CardContent className="p-4">
                    {/* Message Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold",
                          msg.author_role === 'coach' ? 'bg-green-700' : 'bg-stone-500'
                        )}>
                          {msg.author_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-stone-900 text-sm">{msg.author_name}</span>
                            {msg.author_role === 'coach' && (
                              <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">COACH</span>
                            )}
                            {msg.pinned && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5" data-testid={`pin-badge-${msg.id}`}>
                                📌 PINNED
                              </span>
                            )}
                            {msg.is_urgent && (
                              <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
                                <AlertTriangle size={8} /> URGENT
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-stone-400">
                            <Clock size={8} /> {formatRelative(msg.created_at)}
                            {game && (
                              <span className="bg-stone-100 px-1.5 py-0.5 rounded text-stone-500">
                                {game.is_home ? 'vs' : '@'} {game.opponent_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {isAuthor ? (
                        <div className="flex items-center gap-1">
                          {msg.author_role === 'coach' && (
                            <TooltipProvider delayDuration={250}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className={cn("h-7 w-7 p-0", msg.pinned ? "text-amber-500 hover:text-amber-600" : "text-stone-400 hover:text-amber-500")}
                                    onClick={() => handleTogglePin(msg.id, !!msg.pinned)}
                                    data-testid={`pin-msg-${msg.id}`}
                                    aria-label={msg.pinned ? 'Unpin from Parent View' : 'Pin to Parent View'}
                                  >
                                    <span style={{ fontSize: 14 }}>📌</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-[220px] text-center">
                                  {msg.pinned
                                    ? 'Unpin · removes from the Parent View sticky banner'
                                    : '📌 Pin to show this in Parent View as a sticky banner.'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-stone-400 hover:text-red-500"
                            onClick={() => handleDeleteMessage(msg.id)} data-testid={`delete-msg-${msg.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-stone-400 hover:text-stone-700"
                            onClick={() => setOpenMenuId(openMenuId === msg.id ? null : msg.id)}
                            data-testid={`more-msg-${msg.id}`}>
                            <MoreVertical size={14} />
                          </Button>
                          {openMenuId === msg.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                              <div className="absolute right-0 top-8 z-20 bg-white shadow-lg border border-stone-200 rounded-lg overflow-hidden min-w-[180px]" data-testid={`menu-msg-${msg.id}`}>
                                <button
                                  onClick={() => { setOpenMenuId(null); setReportTarget({ messageId: msg.id, authorId: msg.author_id, authorName: msg.author_name }); }}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 text-left"
                                  data-testid={`report-msg-${msg.id}`}>
                                  <Flag size={14} /> Report message
                                </button>
                                <button
                                  onClick={() => blockAuthor(msg.author_id, msg.author_name)}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left border-t border-stone-100"
                                  data-testid={`block-msg-${msg.id}`}>
                                  <Ban size={14} /> Block {msg.author_name}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Message Body */}
                    {msg.text && <p className="text-stone-800 text-sm whitespace-pre-wrap mb-2">{msg.text}</p>}
                    {msg.photo && (
                      <img src={msg.photo} alt="attachment" className="rounded-lg max-h-64 object-cover mb-2 border border-stone-200" />
                    )}

                    {/* Actions Row */}
                    <div className="flex items-center gap-3 pt-2 border-t border-stone-100">
                      <button className="flex items-center gap-1 text-xs text-stone-400 hover:text-green-600 transition-colors"
                        onClick={() => setReplyingTo(replyingTo === msg.id ? null : msg.id)}
                        data-testid={`reply-btn-${msg.id}`}>
                        <Reply size={12} /> Reply
                      </button>
                      {msg.replies?.length > 0 && (
                        <button className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-700 transition-colors"
                          onClick={() => setExpandedMsg(isExpanded ? null : msg.id)}
                          data-testid={`expand-replies-${msg.id}`}>
                          <ChevronDown size={12} className={cn("transition-transform", isExpanded && "rotate-180")} />
                          {msg.replies.length} {msg.replies.length === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                    </div>

                    {/* Replies */}
                    {isExpanded && msg.replies?.length > 0 && (
                      <div className="mt-3 space-y-2 pl-6 border-l-2 border-stone-200">
                        {msg.replies.map(r => {
                          const isMyReply = r.author_id === user?.id;
                          return (
                            <div key={r.id} className="bg-stone-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-stone-800 text-xs">{r.author_name}</span>
                                  <span className="text-[10px] text-stone-400">{formatRelative(r.created_at)}</span>
                                </div>
                                {!isMyReply && (
                                  <button
                                    onClick={() => setReportTarget({ messageId: msg.id, replyId: r.id, authorId: r.author_id, authorName: r.author_name })}
                                    className="text-[10px] text-stone-400 hover:text-red-600 flex items-center gap-1"
                                    data-testid={`report-reply-${r.id}`}>
                                    <Flag size={10} /> Report
                                  </button>
                                )}
                              </div>
                              {r.text && <p className="text-stone-700 text-sm">{r.text}</p>}
                              {r.photo && <img src={r.photo} alt="reply attachment" className="rounded-lg max-h-40 mt-1 border" />}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Reply Input */}
                    {replyingTo === msg.id && (
                      <div className="mt-3 space-y-2 bg-stone-50 rounded-lg p-3" data-testid={`reply-form-${msg.id}`}>
                        {replyPhotoPreview && (
                          <div className="relative inline-block">
                            <img src={replyPhotoPreview} alt="preview" className="h-20 rounded-lg border" />
                            <button className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                              onClick={() => { setReplyPhoto(null); setReplyPhotoPreview(null); }}>
                              <X size={10} />
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Input value={replyText} onChange={e => setReplyText(e.target.value)}
                            placeholder="Write a reply..."
                            className="flex-1 text-sm"
                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendReply(msg.id)}
                            data-testid={`reply-input-${msg.id}`} />
                          <input type="file" accept="image/*" ref={replyFileRef} className="hidden"
                            onChange={e => handlePhotoSelect(e, true)} />
                          <Button size="sm" variant="ghost" className="text-stone-400 hover:text-green-600 px-2"
                            onClick={() => replyFileRef.current?.click()}>
                            <Camera size={16} />
                          </Button>
                          <Button size="sm" disabled={sending} className="bg-green-700 hover:bg-green-600 px-3"
                            onClick={() => handleSendReply(msg.id)} data-testid={`send-reply-${msg.id}`}>
                            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Compose Message Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone size={20} className="text-green-600" />
              New Message
            </DialogTitle>
            <DialogDescription>Send an announcement to your team</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Game Selection */}
            <div>
              <label className="text-xs font-semibold text-stone-500 mb-1 block">SCOPE</label>
              <select value={msgGameId} onChange={e => setMsgGameId(e.target.value)}
                className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm bg-white"
                data-testid="compose-game-select">
                <option value="">Team-wide announcement</option>
                {games.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.is_home ? 'vs' : '@'} {g.opponent_name} — {g.date}
                  </option>
                ))}
              </select>
            </div>

            {/* Urgent Toggle */}
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border transition-all
              hover:border-red-300" data-testid="compose-urgent-toggle">
              <input type="checkbox" checked={msgIsUrgent} onChange={e => setMsgIsUrgent(e.target.checked)}
                className="hidden" />
              <div className={cn(
                "w-10 h-6 rounded-full flex items-center transition-all p-0.5",
                msgIsUrgent ? "bg-red-500 justify-end" : "bg-stone-300 justify-start"
              )}>
                <div className="w-5 h-5 rounded-full bg-white shadow" />
              </div>
              <div>
                <span className={cn("text-sm font-semibold", msgIsUrgent ? "text-red-600" : "text-stone-600")}>
                  <AlertTriangle size={14} className="inline mr-1" />
                  Mark as Urgent
                </span>
                <p className="text-[10px] text-stone-400">Parents will see a priority alert</p>
              </div>
            </label>

            {/* Message Text */}
            <textarea value={msgText} onChange={e => setMsgText(e.target.value)}
              placeholder="Type your message..."
              rows={4}
              className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="compose-text-input" />

            {/* Pin to Parent View — explicit toggle, auto-suggested by keywords like
                "rain", "field change", "moved to" so important announcements aren't lost. */}
            <label htmlFor="msg-pin-toggle" className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50/40 cursor-pointer hover:bg-amber-50 transition" data-testid="compose-pin-toggle-row">
              <div className="flex items-center gap-2 flex-1">
                <input id="msg-pin-toggle" type="checkbox" checked={msgPinOnSend}
                  onChange={e => { setMsgPinOnSend(e.target.checked); setPinAutoSuggested(false); }}
                  data-testid="compose-pin-toggle"
                  className="w-4 h-4 accent-amber-500" />
                <div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={cn("text-sm font-semibold", msgPinOnSend ? "text-amber-700" : "text-stone-600")}>
                      📌 Pin to Parent View
                    </span>
                    {pinAutoSuggested && (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-500 text-white font-bold rounded px-1.5 py-0.5">Suggested</span>
                    )}
                  </div>
                  <p className="text-[10px] text-stone-400 leading-tight">
                    {pinAutoSuggested
                      ? 'This looks important — we&rsquo;ll pin it as a sticky banner for parents.'
                      : 'Show this as a sticky banner at the top of Parent View.'}
                  </p>
                </div>
              </div>
            </label>

            {/* Photo Upload */}
            {msgPhotoPreview ? (
              <div className="relative inline-block">
                <img src={msgPhotoPreview} alt="preview" className="max-h-40 rounded-lg border" />
                <button className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                  onClick={() => { setMsgPhoto(null); setMsgPhotoPreview(null); }}>
                  <X size={12} />
                </button>
              </div>
            ) : (
              <button onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 text-sm text-stone-500 hover:text-green-600 transition-colors p-3 border border-dashed border-stone-300 rounded-lg w-full justify-center"
                data-testid="compose-photo-btn">
                <Camera size={16} /> Attach Photo
              </button>
            )}
            <input type="file" accept="image/*" ref={fileInputRef} className="hidden"
              onChange={e => handlePhotoSelect(e, false)} />

            {/* Send */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button className={cn("flex-1", msgIsUrgent ? "bg-red-600 hover:bg-red-500" : "bg-green-700 hover:bg-green-600")}
                disabled={sending || (!msgText.trim() && !msgPhoto)}
                onClick={handleSendMessage} data-testid="send-message-btn">
                {sending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                {msgIsUrgent ? 'Send Urgent' : 'Send Message'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Dialog (UGC safety — Google Play requirement) */}
      <Dialog open={!!reportTarget} onOpenChange={(open) => { if (!open) { setReportTarget(null); setReportReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag size={18} className="text-red-600" /> Report content
            </DialogTitle>
            <DialogDescription>
              Let us know what's wrong with this {reportTarget?.replyId ? 'reply' : 'message'} from <strong>{reportTarget?.authorName}</strong>. Our team will review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {['Harassment or bullying', 'Hate speech', 'Spam', 'Inappropriate content', 'Other'].map(r => (
              <button
                key={r}
                onClick={() => setReportReason(r)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-md border text-sm transition-colors',
                  reportReason === r
                    ? 'border-red-500 bg-red-50 text-red-700 font-semibold'
                    : 'border-stone-300 hover:border-stone-400 text-stone-700'
                )}
                data-testid={`report-reason-${r.split(' ')[0].toLowerCase()}`}>
                {r}
              </button>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setReportTarget(null); setReportReason(''); }}>Cancel</Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-500 text-white"
              disabled={!reportReason}
              onClick={submitReport}
              data-testid="submit-report-btn">
              Submit report
            </Button>
          </div>
          {reportTarget && (
            <Button
              variant="ghost"
              className="w-full text-stone-600 hover:text-red-600 mt-1"
              onClick={() => { blockAuthor(reportTarget.authorId, reportTarget.authorName); setReportTarget(null); setReportReason(''); }}
              data-testid="report-and-block-btn">
              <Ban size={14} className="mr-2" /> Also block {reportTarget.authorName}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
